import { useMemo } from "react";

/**
 * Responsive, format-optimized image component for CMS-managed content.
 *
 * Renders a native <picture> element with AVIF + WebP <source> tags so the
 * browser picks the smallest supported variant. Explicit width/height are
 * always required (prevents CLS). Alt text is required — the component
 * throws in dev if you forget.
 *
 * Usage:
 *   <MedicalImage
 *      media={mediaObj}       // full response from POST /api/admin/images
 *      sizes="(max-width: 640px) 90vw, 800px"
 *      priority={false}       // true only for above-the-fold hero images
 *      className="rounded-2xl"
 *   />
 *
 * OR when only a URL is available (legacy providers):
 *   <MedicalImage src="/foo.jpg" alt="..." width={800} height={450} />
 */
export default function MedicalImage({
  media,
  src,
  alt: altProp,
  width: widthProp,
  height: heightProp,
  sizes = "(max-width: 640px) 90vw, (max-width: 1024px) 60vw, 800px",
  priority = false,
  className = "",
  testId,
  ...rest
}) {
  const derived = useMemo(() => {
    if (media && media.id) {
      const avif = (media.variants?.avif || []).slice().sort((a, b) => a.w - b.w);
      const webp = (media.variants?.webp || []).slice().sort((a, b) => a.w - b.w);
      // Emit RELATIVE URLs. The backend returns `/api/media/{id}/{size}.webp`
      // (path-only) so `<img src>` resolves against the current origin. This
      // is critical for the Puppeteer prerender: the snapshot must be host-
      // agnostic so a preview-built HTML file still points at the right
      // asset when served from https://medicalpeptides.ai (and vice versa),
      // and the client-side hydration produces the same DOM as the
      // prerender captured (no React #418 mismatch from a swapped host).
      const same = (url) => (url && url.startsWith("http") ? url : url || "");
      const avifSrcSet = avif.map((v) => `${same(v.url)} ${v.w}w`).join(", ");
      const webpSrcSet = webp.map((v) => `${same(v.url)} ${v.w}w`).join(", ");
      const largest = webp[webp.length - 1] || avif[avif.length - 1];
      return {
        alt: media.alt || altProp || "",
        width: media.width,
        height: media.height,
        fallbackSrc: same(largest?.url || media.original?.url || ""),
        avifSrcSet,
        webpSrcSet,
      };
    }
    // Plain-URL mode (legacy imagery hosted externally)
    return {
      alt: altProp || "",
      width: widthProp,
      height: heightProp,
      fallbackSrc: src,
      avifSrcSet: "",
      webpSrcSet: "",
    };
  }, [media, src, altProp, widthProp, heightProp]);

  // Hard requirements — surface them loudly in dev.
  if (process.env.NODE_ENV !== "production") {
    if (!derived.alt) {
      // eslint-disable-next-line no-console
      console.error("[MedicalImage] alt text is required (accessibility + SEO)", { media, src });
    }
    if (!derived.width || !derived.height) {
      // eslint-disable-next-line no-console
      console.warn("[MedicalImage] width/height not set — this will cause layout shift (CLS)", { media, src });
    }
  }

  // Above-the-fold: fetchpriority=high, loading=eager, no lazy.
  // Below-the-fold: lazy load + async decode.
  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";

  return (
    <picture data-testid={testId}>
      {derived.avifSrcSet ? (
        <source type="image/avif" srcSet={derived.avifSrcSet} sizes={sizes} />
      ) : null}
      {derived.webpSrcSet ? (
        <source type="image/webp" srcSet={derived.webpSrcSet} sizes={sizes} />
      ) : null}
      <img
        src={derived.fallbackSrc}
        alt={derived.alt}
        width={derived.width}
        height={derived.height}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        className={className}
        {...rest}
      />
    </picture>
  );
}
